import {
  COMMENT_JOB_TRANSITIONS,
  GUARANTEED_COMMENT_CANCEL_OUTCOMES,
  type AgentCommandStatus,
  type ApproveCommentJobResponse,
  type CancelCommentJobResponse,
  type CommentCancelRecallOutcome,
  type CommentCandidate,
  type CommentJob,
  type CommentJobStatus,
  type CommentRejectionReason,
  type CommentSource,
  type CommentValidation,
  type GenerateCommentResponse,
  type Platform,
  type UnsupportedClaim,
} from '@midiautolistening/contracts';

import type { ApiRequestError } from '@/lib/api';
import {
  COMMENT_JOB_STATUS_LABELS,
  COMMENT_REJECTION_REASON_LABELS,
  UNSUPPORTED_CLAIM_KIND_MESSAGES,
  platformDescriptor,
  type Descriptor,
  type Tone,
} from '@/lib/labels';
import type { ConfirmOptions, EmergencyStopResponse } from '@/stores/ui';

/**
 * The decisions the comment screens make, kept out of the SFCs so they can be
 * tested. Nothing here talks HTTP and nothing imports Vue.
 *
 * THE RULE THIS FILE ENFORCES: whether an action is offered is the server's
 * answer, not ours. `allowedTransitions` comes straight out of
 * `COMMENT_JOB_TRANSITIONS[job.status]` on the API side, and a client that
 * re-derived it would drift the first time an edge changed — offering an
 * "Duyệt" button that answers 409, or hiding one that would have worked.
 */

// ---------------------------------------------------------------------------
// What the operator may do to a job
// ---------------------------------------------------------------------------

/** Anything that carries a job status: a `CommentJob`, or a list row. */
export type JobStatusLike = Pick<CommentJob, 'status'>;

/**
 * Transitions to test against, given what the caller was actually handed.
 *
 * `GET /comment-jobs/:id` returns `allowedTransitions`; `GET /comment-jobs`
 * does **not** — `commentJobListItemSchema` has no such field. So the approval
 * queue, which is the screen most likely to want an inline "Duyệt" button, has
 * no server verdict to read.
 *
 * The fallback is the shared table from `@midiautolistening/contracts` — the
 * very object the API builds its response from — and not a second copy of the
 * state machine written here. That distinction is the whole point: when an edge
 * changes in contracts, both sides change together. A list row is still one
 * refresh staler than the detail read, so when a real `allowedTransitions` is
 * available it always wins.
 */
function transitionsFor(
  job: JobStatusLike,
  allowedTransitions?: readonly CommentJobStatus[] | null,
): readonly CommentJobStatus[] {
  return allowedTransitions ?? COMMENT_JOB_TRANSITIONS[job.status];
}

/**
 * `POST /comment-jobs/:id/approve` has two entrances, and this mirrors both.
 *
 * From WAITING_APPROVAL it moves the job to APPROVED — never straight to
 * QUEUED, because APPROVED is the state that records who signed off. From
 * USER_ACTION_REQUIRED and FAILED it walks the machine's own retry edge
 * straight to QUEUED: the blocker was on the operator's machine (a paused
 * browser, a checkpoint they have since cleared), and pressing the button again
 * is the human action the status was waiting for. Before the second entrance
 * existed those jobs showed a disabled "Duyệt & gửi" with no way forward but
 * Huỷ (the browser-PAUSED refusal of 20/08/2026).
 */
const REDISPATCHABLE_STATUSES: readonly CommentJobStatus[] = ['USER_ACTION_REQUIRED', 'FAILED'];

export function canApprove(
  job: JobStatusLike,
  allowedTransitions?: readonly CommentJobStatus[] | null,
): boolean {
  const transitions = transitionsFor(job, allowedTransitions);
  if (transitions.includes('APPROVED')) return true;
  return REDISPATCHABLE_STATUSES.includes(job.status) && transitions.includes('QUEUED');
}

/**
 * Cancel stays available well past approval — QUEUED and SENT_TO_AGENT can both
 * still be recalled — and stops only once the agent reports back. A cancel
 * button that vanished at approval would strand a job the operator can still
 * pull.
 */
export function canCancel(
  job: JobStatusLike,
  allowedTransitions?: readonly CommentJobStatus[] | null,
): boolean {
  return transitionsFor(job, allowedTransitions).includes('CANCELLED');
}

// ---------------------------------------------------------------------------
// Reading a validation verdict
// ---------------------------------------------------------------------------

export interface RejectionEntry {
  reason: CommentRejectionReason;
  descriptor: Descriptor;
  /** The server's specifics for this reason. Null when it sent none. */
  detail: string | null;
}

/**
 * Pair `rejectionReasons` with `details`, which the contract promises are in the
 * same order.
 *
 * Paired by index rather than zipped blindly: the arrays are built from one
 * `map` each server-side and should always match, but a short `details` would
 * throw on `.length` mismatch inside a template and take the whole panel down —
 * over a *label*, on the screen whose job is to explain a refusal. A missing
 * detail degrades to the code's own label instead.
 */
export function rejectionEntries(validation: CommentValidation): RejectionEntry[] {
  return validation.rejectionReasons.map((reason, index) => ({
    reason,
    descriptor: COMMENT_REJECTION_REASON_LABELS[reason],
    detail: validation.details[index] ?? null,
  }));
}

// ---------------------------------------------------------------------------
// What a reviewer must see before pressing send
// ---------------------------------------------------------------------------

/**
 * A candidate, as it now arrives.
 *
 * `reviewFlags` and `unsupportedClaims` used to be declared here as optional
 * extras, because `commentCandidateSchema` did not carry them and the store had
 * to parse through a widened copy of it to keep them alive. Both fields are on
 * the contract now — required, never `undefined`, backfilled to `[]` for every
 * row written before the migration — so the bridge is gone and this alias is all
 * that remains of it. It exists only so the two comment screens keep compiling
 * this round; new code should say `CommentCandidate`.
 */
export type ReviewedCandidate = CommentCandidate;

/**
 * How loudly a notice must be drawn — the array order IS the ranking.
 *
 *  - `BLOCKING`      — the server refused this body; it cannot go out as written.
 *  - `SERIOUS`       — this draft asserts something nobody has substantiated.
 *  - `CAUTION`       — something about this draft is under the project's bar.
 *  - `CONFIGURATION` — a fact about the project. Not a problem, and not red.
 *
 * Screens are expected to render `CONFIGURATION` as ordinary status text and
 * everything above it as an alert. Keeping the two in one list, distinguished by
 * a field, is deliberate: they are answers to the same question ("what should I
 * know before I press send?") and splitting them into two functions invites a
 * screen to render one and forget the other.
 */
export const DRAFT_WARNING_SEVERITIES = [
  'BLOCKING',
  'SERIOUS',
  'CAUTION',
  'CONFIGURATION',
] as const;
export type DraftWarningSeverity = (typeof DRAFT_WARNING_SEVERITIES)[number];

/**
 * One code per thing that can be said about a draft — and no code for "the
 * server held this and would not say why".
 *
 * There used to be a fifth, `REQUIRES_MANUAL_REVIEW`, for the round in which
 * `reviewFlags` was not on the wire yet and `requiresManualReview` was the only
 * signal. It was deleted rather than kept as a fallback: it fired on 100% of AI
 * drafts — the product's default mode — and it fired *alongside* the real
 * warnings, so a reviewer read "độ tin cậy 42% dưới ngưỡng 80%" and then, one
 * line below, "máy chủ không nói rõ vì độ tin cậy hay vì gì". Two notices, one
 * contradicting the other, on the screen whose only job is to be believed.
 */
export type DraftWarningCode =
  | 'VALIDATION_FAILED'
  | 'UNSUPPORTED_CLAIM'
  | 'LOW_CONFIDENCE'
  | 'PROJECT_REQUIRES_APPROVAL';

export interface DraftWarning {
  code: DraftWarningCode;
  severity: DraftWarningSeverity;
  /** Vietnamese, safe to render verbatim. */
  message: string;
  /** The server's own words for this warning, when it wrote any. */
  detail: string | null;
  /**
   * The exact phrase the draft used, on `UNSUPPORTED_CLAIM` and nothing else.
   *
   * Carried as its own field rather than left buried in `message` so a screen can
   * put it in a `<mark>`, and so nothing has to parse a sentence back apart.
   */
  quote: string | null;
  claimKind: UnsupportedClaim['kind'] | null;
  tone: Tone;
}

export interface DraftWarningOptions {
  /**
   * `commentSettings.minAiConfidence` for this project.
   *
   * It no longer decides anything: whether a draft is low-confidence is the
   * server's `LOW_CONFIDENCE` flag, computed against the project's own threshold,
   * and a client that re-derived the verdict would disagree with the API the
   * moment somebody changed the setting between the draft and the read.
   *
   * It is still worth passing, for one thing only — putting the second number
   * into the sentence ("42% thấp hơn ngưỡng 80%"), which is what turns a verdict
   * into something a reviewer can act on. Absent, the warning still fires and
   * simply says less.
   */
  minConfidence?: number | null;
}

/**
 * Who put a phrase in the draft.
 *
 * "Mô hình viết" is wrong on a template draft: the operator wrote that sentence
 * themselves, and telling somebody a model invented their own price is exactly
 * the kind of detail that costs a warning its credibility.
 */
function claimAuthor(source: CommentSource): string {
  return source === 'AI' ? 'Mô hình viết' : 'Bản nháp viết';
}

/** Say it with both numbers when both are known, and never invent the missing one. */
function lowConfidenceMessage(confidence: number | null, minConfidence: number | null): string {
  if (confidence === null) {
    return 'Máy chủ đánh giá bản nháp này chưa đủ chắc chắn nên giữ lại cho người đọc.';
  }
  if (minConfidence === null) {
    return `Độ tin cậy của bản nháp là ${formatConfidence(confidence)}, dưới ngưỡng của dự án.`;
  }
  return `Độ tin cậy ${formatConfidence(confidence)} thấp hơn ngưỡng ${formatConfidence(minConfidence)} của dự án.`;
}

/**
 * Everything the approver has to read before the comment goes out under their
 * name, worst first.
 *
 * WHAT THIS FUNCTION IS FOR. It used to treat `candidate.requiresManualReview`
 * as a signal about the draft in front of you. It is not one. The API sets it to
 * `draft.requiresManualReview || draftDecision.requiresApproval || lowConfidence`
 * and `requiresApproval` is `mode === 'AI_DRAFT' || (settings?.requireApproval ??
 * true)` — so in both of the product's default modes the flag is true on every
 * draft ever produced. Thirty comments written from the operator's own templates
 * got the identical red block thirty times; the thirty-first, which really did
 * promise a discount nobody offers, looked exactly the same. People learn to
 * skip a block like that by the third repetition, and that is the failure this
 * rewrite exists to end: configuration is reported as configuration, and only
 * what is true of THIS draft is allowed to be an alert.
 *
 * `reviewFlags` carries that split and is the ONLY thing read here.
 * `requiresManualReview` is deliberately not consulted at all — not even as a
 * fallback for a candidate created before the flags existed. Those rows were
 * backfilled to `[]` by the migration, so they answer "nothing is wrong with
 * this draft", and the choice to believe them is the choice to stay quiet: a
 * fallback that turns "the server told us nothing" into a coloured block is how
 * the boy-who-cried-wolf came back the last two times this file was reviewed.
 * Silence about an old row costs one reader one re-read; noise on every row
 * costs every reader every warning.
 */
export function draftWarnings(
  candidate: CommentCandidate | null,
  options: DraftWarningOptions = {},
): DraftWarning[] {
  if (!candidate) return [];

  const warnings: DraftWarning[] = [];

  /**
   * Hard refusals first: the server will not send this body as written.
   *
   * Severity is BLOCKING for all of them while the tone still comes from the
   * reason's own descriptor. Two different questions: severity answers "can it go
   * now?", tone answers "will editing help?" — and a rate limit, which is amber
   * in `COMMENT_REJECTION_REASON_LABELS` because waiting fixes it, must not turn
   * into a demand that somebody rewrite a perfectly good comment.
   */
  if (!candidate.validation.passed) {
    for (const entry of rejectionEntries(candidate.validation)) {
      warnings.push({
        code: 'VALIDATION_FAILED',
        severity: 'BLOCKING',
        message: entry.descriptor.label,
        detail: entry.detail,
        quote: null,
        claimKind: null,
        tone: entry.descriptor.tone,
      });
    }
  }

  /**
   * One warning per claim, each carrying its own phrase.
   *
   * A paragraph explaining that models sometimes invent prices is a paragraph
   * people skip. «giảm 30%», quoted from the text they are about to publish, is
   * not — it names the sentence to delete. The claim is never called false: the
   * check knows only that the phrase appears nowhere in the operator's own
   * material, and the operator is the only one who can judge the rest.
   */
  const flags = candidate.reviewFlags;
  const claims = candidate.unsupportedClaims;

  for (const claim of claims) {
    warnings.push({
      code: 'UNSUPPORTED_CLAIM',
      severity: 'SERIOUS',
      message: `${claimAuthor(candidate.source)} «${claim.quote}» — ${UNSUPPORTED_CLAIM_KIND_MESSAGES[claim.kind]}`,
      detail: null,
      quote: claim.quote,
      claimKind: claim.kind,
      tone: 'danger',
    });
  }

  // The flag raised with its evidence array missing. The contract carries both,
  // so this should not happen — but losing the single most serious signal on the
  // screen because the quotes did not arrive is not a trade this may make. Say
  // less, still say it.
  if (claims.length === 0 && flags.includes('UNSUPPORTED_CLAIM')) {
    warnings.push({
      code: 'UNSUPPORTED_CLAIM',
      severity: 'SERIOUS',
      message:
        'Bản nháp này có khẳng định chưa có cơ sở trong tư liệu thương hiệu của bạn, nhưng máy chủ không gửi kèm câu cụ thể — hãy đọc lại toàn bộ nội dung, đặc biệt là mọi con số.',
      detail: null,
      quote: null,
      claimKind: null,
      tone: 'danger',
    });
  }

  const minConfidence = options.minConfidence ?? null;

  /**
   * The server's verdict, and nothing else.
   *
   * The client used to compare `confidence` against the threshold it had been
   * handed whenever the flag was missing. That comparison is gone with the
   * fallback that needed it: the API already made this judgement against the
   * project's stored `min_ai_confidence`, and a second opinion computed from a
   * number this screen may have loaded minutes earlier can only ever disagree
   * with the row the operator is looking at.
   */
  if (flags.includes('LOW_CONFIDENCE')) {
    warnings.push({
      code: 'LOW_CONFIDENCE',
      severity: 'CAUTION',
      message: lowConfidenceMessage(candidate.confidence, minConfidence),
      detail: null,
      quote: null,
      claimKind: null,
      tone: 'warning',
    });
  }

  // Configuration, stated as configuration. "Dự án đặt chế độ luôn cần người
  // duyệt" is useful and true; painted red it is the boy who cried wolf.
  if (flags.includes('PROJECT_REQUIRES_APPROVAL')) {
    warnings.push({
      code: 'PROJECT_REQUIRES_APPROVAL',
      severity: 'CONFIGURATION',
      message: 'Dự án này đặt chế độ luôn cần người duyệt trước khi bình luận được gửi đi.',
      detail: null,
      quote: null,
      claimKind: null,
      tone: 'info',
    });
  }

  /**
   * There is deliberately no branch here for `candidate.requiresManualReview`.
   *
   * The one that stood in this spot ran whenever `reviewFlags` was absent, and
   * it was written when absent was the normal case. It survived one round too
   * many: with the flags on the wire it kept firing on the pre-flag rows, and on
   * an AI draft it emitted a CAUTION saying the server had not explained itself
   * — directly under the LOW_CONFIDENCE warning where the server had just
   * explained itself, with both numbers. Nothing replaces it. `reviewFlags` is
   * the whole answer, `[]` means "nothing to say", and this function says
   * nothing.
   */

  // The pushes above already run worst-first; the sort keeps that true after the
  // next edit rather than depending on somebody noticing the order.
  return warnings.sort(
    (left, right) =>
      DRAFT_WARNING_SEVERITIES.indexOf(left.severity) -
      DRAFT_WARNING_SEVERITIES.indexOf(right.severity),
  );
}

// ---------------------------------------------------------------------------
// What just happened when the operator pressed a button
// ---------------------------------------------------------------------------

/**
 * One shape for every "what did that click actually do" answer.
 *
 * Three screens report on three endpoints — soạn, duyệt, huỷ — and each of them
 * used to compose its own sentence at the call site. Four of the bugs found in
 * two review rounds were the same bug in different words: a screen that read one
 * field, believed the cheerful reading of it, and said so in green. So the
 * sentence is written once per endpoint, here, where it can be tested; a screen
 * picks a toast colour from `tone` and prints the two strings.
 */
export interface ActionOutcome {
  tone: Tone;
  /** Vietnamese, one line. */
  title: string;
  /** Vietnamese: what is true now, and what to do next when there is something. */
  detail: string;
}

export type ApproveOutcome = ActionOutcome;

/**
 * The base sentence for each command status. `Record` so a new member of
 * `AGENT_COMMAND_STATUSES` breaks this build instead of falling through to a
 * silent default on the screen that reports a real comment being published.
 *
 * NOT ONE OF THEM IS GREEN, and that is the rule from `labels.ts`: only a job in
 * SUCCESS claims something reached the platform, because the agent is the sole
 * witness to that. Everything here describes a *command*, and "the command left
 * the server" is a promise the server cannot keep.
 *
 * The `detail` strings never name who approved the job. That is not tidiness:
 * {@link describeGenerateOutcome} reuses them for the automatic path, where the
 * server approved it and the operator has not read a word — a detail that said
 * "lúc bạn bấm duyệt" there would be describing a click that never happened.
 *
 * Each entry is a function of the CHANNEL LABEL ("Facebook" / "Threads") because
 * two of the sentences name the platform the comment would land on, and a
 * Threads job that promised something about Facebook is how this parameter was
 * born (a real report, 20/08/2026). Entries that never say the name take no
 * argument — the type allows it, and it keeps the noise out.
 */
const APPROVE_OUTCOME_BY_COMMAND: Record<AgentCommandStatus, (channel: string) => ApproveOutcome> =
  {
    PENDING: () => ({
      tone: 'warning',
      title: 'Đã duyệt, nhưng lệnh chưa rời máy chủ',
      detail:
        'Agent không trực tuyến lúc lệnh được tạo nên khung lệnh mới chỉ được ghi lại, chưa gửi đi. ' +
        'Máy chủ không tự gửi lại khi agent kết nối và lệnh sẽ tự hết hạn, nên hãy bật agent rồi mở lại job này; ' +
        'nếu nó vẫn nằm im, huỷ job và soạn bình luận mới cho bài viết.',
    }),
    SENT: (channel) => ({
      tone: 'info',
      title: 'Đã duyệt và đẩy lệnh sang agent',
      detail:
        `Lệnh đã rời máy chủ nhưng agent chưa báo lại gì. Chỉ agent biết bình luận có thật sự lên ${channel} hay không, ` +
        'nên hãy chờ job chuyển sang "Đã đăng" rồi mới coi là xong.',
    }),
    ACKNOWLEDGED: () => ({
      tone: 'info',
      title: 'Agent đã nhận lệnh bình luận',
      detail:
        'Agent xác nhận nhận được lệnh và sẽ tự gõ bình luận trong cửa sổ Chromium. ' +
        'Kết quả chỉ chắc chắn khi agent báo về.',
    }),
    COMPLETED: (channel) => ({
      tone: 'info',
      title: 'Agent báo đã xử lý xong lệnh',
      detail:
        `Lệnh đã đóng ở phía agent. Đó vẫn chưa phải bằng chứng bình luận đã lên ${channel} — ` +
        'bằng chứng nằm ở phần kết quả của job, nơi có liên kết tới bình luận thật.',
    }),
    FAILED: () => ({
      tone: 'danger',
      title: 'Đã duyệt nhưng lệnh bình luận thất bại',
      detail: 'Không có gì được đăng. Xem lỗi cụ thể ở phần kết quả của job.',
    }),
    REJECTED: () => ({
      tone: 'danger',
      title: 'Agent từ chối lệnh bình luận',
      detail:
        'Bộ chặn chạy ngay tại máy bạn đã từ chối, và quyết định đó được ưu tiên hơn tính toán của máy chủ. ' +
        'Không có bình luận nào được đăng; lý do nằm ở phần kết quả của job.',
    }),
    EXPIRED: () => ({
      tone: 'warning',
      title: 'Lệnh hết hạn trước khi agent nhận',
      detail:
        'Lệnh bình luận chỉ sống được vài phút để không bị phát lại về sau, và khoảng đó đã trôi qua. ' +
        'Không có gì được đăng.',
    }),
  };

/** The contract allows it; today's server never produces it. See the note below. */
const APPROVE_OUTCOME_NO_COMMAND: ApproveOutcome = {
  tone: 'warning',
  title: 'Đã duyệt, nhưng máy chủ không tạo lệnh nào',
  detail:
    'Job đã rời hàng chờ duyệt mà không có mã lệnh nào quay về, nên chưa có gì tới được agent. ' +
    'Đừng coi bình luận là đã đăng; kiểm tra trạng thái agent rồi xem lại job.',
};

/**
 * What a *successful* approve response actually means, in one honest sentence.
 *
 * The screens used to branch on `commandId === null` and call everything else
 * "Đã duyệt và gửi lệnh bình luận", in green. Both halves were wrong.
 * `dispatch()` in `comments.service.ts` always returns a ULID `commandId` and
 * sets `commandStatus = issued.delivered ? 'SENT' : 'PENDING'`; when the agent is
 * offline `delivered` is false, the frame never touches the socket, the job stops
 * at QUEUED with `sentToAgentAt: null` and the rate-limit slot is refunded. So
 * the green toast fired on precisely the case where nothing had been sent, and
 * the carefully written `commandId === null` branch was dead code.
 *
 * `commandStatus` is the field that knows, so it is the field this reads.
 *
 * TWO THINGS THIS FUNCTION IS NOT FOR:
 *   - A refusal. A rate limit at approve time throws 422 POLICY_REJECTED before
 *     anything is dispatched, and the job stays in WAITING_APPROVAL — which means
 *     the operator DOES have to approve it again later. That belongs in the
 *     caller's catch branch, and the old copy that told them the opposite is the
 *     other half of this bug.
 *   - `validation.passed`. It is always true here: `approveJob` throws on a
 *     failed verdict, so branching on it would add exactly the sort of dead,
 *     confident-sounding branch this function was written to delete.
 */
export function describeApproveOutcome(response: ApproveCommentJobResponse): ApproveOutcome {
  const base =
    response.commandId === null || response.commandStatus === null
      ? APPROVE_OUTCOME_NO_COMMAND
      : APPROVE_OUTCOME_BY_COMMAND[response.commandStatus](
          platformDescriptor(response.job.platform).label,
        );

  // The job's own status is appended rather than described in prose: it is the
  // row the operator is looking at, and it is the server's word, not ours.
  const jobStatus = COMMENT_JOB_STATUS_LABELS[response.job.status].label;
  return { ...base, detail: `${base.detail} Trạng thái job hiện tại: "${jobStatus}".` };
}

// ---------------------------------------------------------------------------
// What just happened when the operator pressed Soạn
// ---------------------------------------------------------------------------

/**
 * Whether pressing Soạn produced something to read, or something that is gone.
 *
 *  - `REFUSED` — the body failed validation. No job, no command, nothing to
 *    approve and nothing to recall.
 *  - `AWAITING_REVIEW` — a job exists and is waiting for a person. The normal
 *    path, and the only one where the dialog's Duyệt button means anything.
 *  - `DISPATCH_STALLED` — the project is automatic, the server approved it
 *    itself, and the frame never left the process. Nothing is on its way, and
 *    nothing will be: the server does not redeliver a command.
 *  - `DISPATCHED` — the project is automatic and the frame reached the agent.
 *    The comment may already be under the operator's name on a stranger's post.
 */
export type GenerateDisposition =
  | 'REFUSED'
  | 'AWAITING_REVIEW'
  | 'DISPATCH_STALLED'
  | 'DISPATCHED';

export interface GenerateOutcome extends ActionOutcome {
  disposition: GenerateDisposition;
}

/** Said before the command sentence on every automatic path, and only there. */
const AUTO_APPROVED_LEAD =
  'Dự án đang ở chế độ tự động nên máy chủ tự duyệt bản nháp này — không có bước để bạn đọc trước.';

/**
 * What pressing Soạn actually did.
 *
 * `jobId` was the only field a screen could read, and it answers the wrong
 * question twice over. It is null on the approval path *with a real job sitting
 * in WAITING_APPROVAL*, and it used to be non-null for a command that had been
 * written but never sent — so the dialog announced "Bình luận đã được gửi đi
 * ngay" over an agent that was offline and a QUEUED job nobody would ever pick
 * up. The server now fills `commandId` / `commandStatus` for exactly this, and
 * they are what this reads:
 *
 *   null      → nobody tried to dispatch; a person still decides.
 *   'PENDING' → the server decided, and the frame did not leave the building.
 *   'SENT'    → the server decided and the frame is on the agent's socket.
 *
 * The other five statuses cannot occur in a response built in the same request
 * that created the command. They are still handled — through the same
 * per-status Record the approve toast uses, so each of them keeps its own exact
 * sentence — and they group under `DISPATCHED` because they share the one fact
 * a screen must not miss: the decision was taken without the operator.
 */
export function describeGenerateOutcome(
  response: GenerateCommentResponse,
  /** The post's channel. Defaulted because the response frame does not carry it. */
  platform: Platform = 'FACEBOOK',
): GenerateOutcome {
  if (!response.candidate.validation.passed) {
    return {
      disposition: 'REFUSED',
      tone: 'danger',
      title: 'Máy chủ từ chối nội dung này',
      detail:
        'Không có lệnh bình luận nào được tạo, nên không có gì để duyệt và cũng không có gì được gửi đi. ' +
        'Lý do nằm ngay dưới bản nháp.',
    };
  }

  const channel = platformDescriptor(platform).label;
  const status = response.commandStatus;
  if (status === null) {
    return {
      disposition: 'AWAITING_REVIEW',
      tone: 'info',
      title: 'Đã soạn xong bản nháp, chưa gửi đi đâu cả',
      detail:
        `Bản nháp đang chờ bạn đọc rồi bấm duyệt; chưa có gì tới ${channel}. ` +
        'Đóng hộp thoại không làm nó mất — nó nằm ở màn hình Bình luận, mục chờ duyệt.',
    };
  }

  const base = APPROVE_OUTCOME_BY_COMMAND[status](channel);
  return {
    disposition: status === 'PENDING' ? 'DISPATCH_STALLED' : 'DISPATCHED',
    /**
     * `info` is upgraded to `warning`, and only here.
     *
     * On the approve path a calm colour is right: the operator read the text and
     * pressed the button. On this path something irreversible happened to a body
     * they have not read yet, so the quietest this may be is amber. `danger`
     * stays `danger` — never downgraded.
     */
    tone: base.tone === 'info' ? 'warning' : base.tone,
    title: base.title,
    detail: `${AUTO_APPROVED_LEAD} ${base.detail}`,
  };
}

// ---------------------------------------------------------------------------
// What just happened when the operator pressed Huỷ
// ---------------------------------------------------------------------------

export interface CancelOutcome extends ActionOutcome {
  /**
   * True only when the server proved nothing will be posted.
   *
   * A screen may gate a reassuring sentence on this and on nothing else. It is
   * NOT `response.guaranteedNotPosted` copied through — see the function.
   */
  guaranteedNotPosted: boolean;
}

/**
 * The client's half of the cancel sentence, per outcome. `detail` is a fallback
 * for a response whose `recallMessage` arrives empty — the server owns that
 * sentence and says it better, because it knows whether the socket write landed.
 *
 * Two of the four are green, which is the one place in this module where green
 * is allowed: `labels.ts` reserves it for a fact the agent witnessed, and this
 * is the mirror image of that rule — the server can prove a *negative* here
 * (there is no command, or the command was revoked before any socket saw it) in
 * a way it can never prove the positive. The other two are not degrees of the
 * same thing; they are the states where the instruction had already left and the
 * comment may be typed anyway, so neither may look like success.
 */
const CANCEL_OUTCOME_COPY: Record<CommentCancelRecallOutcome, (channel: string) => ActionOutcome> =
  {
    NOTHING_TO_RECALL: () => ({
      tone: 'success',
      title: 'Đã huỷ — chưa có lệnh nào được phát',
      detail:
        'Job chưa từng được đẩy sang agent nên không có gì để thu hồi và không có gì được đăng.',
    }),
    COMMAND_REVOKED_BEFORE_SEND: () => ({
      tone: 'success',
      title: 'Đã huỷ và thu hồi lệnh trước khi nó rời máy chủ',
      detail:
        'Lệnh đã được ghi nhưng chưa bao giờ được gửi đi, và giờ nó bị đánh dấu thu hồi nên không chạy được nữa.',
    }),
    RECALL_SENT: (channel) => ({
      tone: 'warning',
      title: 'Đã huỷ job, đã gửi lệnh thu hồi — nhưng không chắc kịp',
      detail:
        'Lệnh bình luận đã ở máy bạn từ trước. Máy chủ vừa gửi thêm một khung thu hồi vào cùng đường truyền, ' +
        `nhưng nếu agent đã bắt đầu gõ thì bình luận vẫn lên ${channel}.`,
    }),
    RECALL_UNDELIVERABLE: () => ({
      tone: 'danger',
      title: 'Đã huỷ job nhưng KHÔNG thu hồi được lệnh',
      detail:
        'Lệnh bình luận đã ở máy bạn từ trước và lúc này agent không kết nối, nên ngay cả thông báo thu hồi cũng không gửi được. ' +
        'Bình luận vẫn có thể được đăng.',
    }),
  };

/**
 * The wording for a response that names a guaranteed outcome while its own
 * `guaranteedNotPosted` says otherwise. See {@link describeCancelOutcome}.
 */
const CANCEL_OUTCOME_UNPROVEN: ActionOutcome = {
  tone: 'warning',
  title: 'Đã huỷ job, nhưng chưa chắc bình luận sẽ không được đăng',
  detail:
    'Máy chủ báo huỷ xong mà không khẳng định là đã chặn được lệnh, nên phải coi như lệnh vẫn có thể chạy.',
};

/** True of every non-guaranteed cancel, and it is the only thing that reaches a run in progress. */
const CANCEL_CHECK_THE_POST =
  'Hãy mở bài viết kiểm tra; muốn chặn chắc chắn thì bấm “Dừng khẩn cấp” trên thanh trên cùng — đó là thứ duy nhất dừng được một lượt gõ đang chạy.';

/**
 * What pressing Huỷ actually stopped.
 *
 * Cancelling always writes CANCELLED to the row, and both screens used to report
 * that in green: `ui.notifySuccess('Đã huỷ job', …)`. But
 * `COMMENT_JOB_TRANSITIONS` allows SENT_TO_AGENT → CANCELLED, so the button is
 * live while the `comment.execute` frame is already on the operator's machine
 * and the agent may be typing. People read "Đã huỷ job" and walked away from a
 * comment that then appeared on a stranger's post under their real name.
 *
 * `recallOutcome` and `guaranteedNotPosted` are the server's answer to the
 * second, different question, and this function requires them to AGREE before it
 * says anything reassuring. Belt and braces on purpose: the two are computed
 * from different things server-side, one of them is a boolean that is easy to
 * default true in a future refactor, and the direction of the mistake is not
 * symmetric. Claiming "đã chặn" about a comment that goes out is the bug this
 * whole vocabulary was added to end; claiming "chưa chắc" about one that was
 * safely stopped costs a reader one look at the post.
 *
 * `recallMessage` is the server's own Vietnamese and is rendered verbatim when
 * it sent one — it knows things this client does not, such as whether a socket
 * write succeeded.
 */
export function describeCancelOutcome(
  response: CancelCommentJobResponse,
  /** The job's channel. Defaulted because the response frame does not carry it. */
  platform: Platform = 'FACEBOOK',
): CancelOutcome {
  const claimsGuarantee = GUARANTEED_COMMENT_CANCEL_OUTCOMES.includes(response.recallOutcome);
  const guaranteed = claimsGuarantee && response.guaranteedNotPosted;
  const copy =
    claimsGuarantee && !guaranteed
      ? CANCEL_OUTCOME_UNPROVEN
      : CANCEL_OUTCOME_COPY[response.recallOutcome](platformDescriptor(platform).label);

  const message = response.recallMessage.trim();
  const jobStatus = COMMENT_JOB_STATUS_LABELS[response.status].label;
  const detail = [
    message === '' ? copy.detail : message,
    // Not appended when the recall is provable: on those two outcomes there is
    // nothing to check and nothing for the kill switch to stop, and a sentence
    // that fires on all four is a sentence nobody reads on the two that matter.
    ...(guaranteed ? [] : [CANCEL_CHECK_THE_POST]),
    `Trạng thái job hiện tại: "${jobStatus}".`,
  ].join(' ');

  return { tone: copy.tone, title: copy.title, detail, guaranteedNotPosted: guaranteed };
}

// ---------------------------------------------------------------------------
// The switch every other sentence on these screens points at
// ---------------------------------------------------------------------------

/**
 * WHY THIS SECTION EXISTS. Three places in this codebase tell a frightened
 * operator to "bật Emergency Stop — đó là thứ duy nhất dừng được một lượt gõ
 * đang chạy": {@link CANCEL_CHECK_THE_POST} above, the DISPATCHED notice in
 * `comment-draft`, and the cancel prompt on the jobs screen. Until this round
 * nothing in the dashboard called `POST /comments/emergency-stop` at all. The
 * banner in `AppShell` draws the state, the settings form prints it, the source
 * picker chips it — and not one of them could turn it on. Every one of those
 * sentences pointed at a button that did not exist, on the screen a person
 * reaches when a comment is about to go out under their real name.
 *
 * The action now lives in `stores/ui`. The words live here, once, for the same
 * reason `describeApproveOutcome` does: two screens are about to grow this
 * button, and two screens writing their own copy for an irreversible workspace
 * action is how the copy drifts apart.
 */

/** Which of the two endpoints a failure came from. They are not symmetric. */
export type EmergencyStopAction = 'ACTIVATE' | 'CLEAR';

/**
 * Asked before the switch is thrown.
 *
 * It names the destruction rather than the intent, because the destruction is
 * the part nobody expects: `CANCELLABLE_ON_EMERGENCY` covers PENDING through
 * SENT_TO_AGENT, every one of those jobs is written CANCELLED, and CANCELLED has
 * no outgoing edge in `COMMENT_JOB_TRANSITIONS`. There is no "resume" — the work
 * is gone and has to be drafted again.
 */
export const EMERGENCY_STOP_CONFIRM: ConfirmOptions = {
  title: 'Dừng khẩn cấp toàn bộ workspace?',
  message: [
    'Công tắc này ghi cờ dừng cho MỌI dự án trong workspace, không riêng dự án bạn đang mở.',
    'Ngay khi bấm: mọi lệnh bình luận đang chờ — chưa duyệt, đã duyệt, đang xếp hàng, và cả lệnh đã gửi sang agent — bị đánh dấu huỷ vĩnh viễn. Huỷ rồi thì không gửi lại được, muốn đăng phải soạn bản nháp mới. Dự án đang chạy bị chuyển sang tạm dừng và máy chủ gửi lệnh dừng tới agent.',
    'Điều nó KHÔNG làm được: nếu agent đã bắt đầu gõ một bình luận, nó chỉ dừng khi nhận được lệnh dừng đó — agent ngoại tuyến thì không nhận được gì. Bấm xong vẫn nên mở bài viết kiểm tra.',
    'Bật thì thành viên nào cũng bật được, nhưng tắt lại là quyền của chủ workspace hoặc quản trị viên.',
  ].join('\n\n'),
  confirmLabel: 'Dừng khẩn cấp',
  cancelLabel: 'Không, để tôi xem lại',
  danger: true,
};

/**
 * Asked before the switch is released — and `danger` on purpose.
 *
 * Turning automation back on for a whole workspace is the direction that starts
 * typing on strangers' posts again; a calm dialog here would be the odd one out
 * in a file whose entire subject is not understating what a click does.
 */
export const EMERGENCY_STOP_CLEAR_CONFIRM: ConfirmOptions = {
  title: 'Tắt dừng khẩn cấp?',
  message: [
    'Bình luận sẽ được phép gửi trở lại trên mọi dự án của workspace.',
    'Tắt công tắc không hoàn tác gì cả: những lệnh bình luận bị huỷ lúc bật thì vẫn huỷ, và những dự án bị chuyển sang tạm dừng vẫn nằm im cho tới khi bạn tự chạy lại từng dự án.',
    'Chỉ tắt khi bạn đã biết vì sao nó được bật.',
  ].join('\n\n'),
  confirmLabel: 'Tắt dừng khẩn cấp',
  cancelLabel: 'Giữ nguyên',
  danger: true,
};

/**
 * What flipping the switch actually did.
 *
 * Not green, in either direction, and for the rule that governs this whole
 * module: the server can promise that no *further* comment will be dispatched,
 * and it cannot promise anything about a run the agent has already started —
 * `emergencyStop` writes the flag and issues `project.pause`, and a `project.pause`
 * to an offline agent is a row in a table. The counts are printed because they
 * are the part the server does know exactly.
 */
export function describeEmergencyStopOutcome(response: EmergencyStopResponse): ActionOutcome {
  if (!response.active) {
    return {
      tone: 'info',
      title: 'Đã tắt dừng khẩn cấp',
      detail:
        `Cờ dừng đã được gỡ khỏi ${response.affectedProjectIds.length} dự án, nên bình luận có thể được gửi trở lại. ` +
        'Việc tắt không hoàn tác gì: lệnh đã huỷ thì huỷ hẳn, và dự án bị tạm dừng vẫn tạm dừng cho tới khi bạn tự chạy lại.',
    };
  }

  const cancelled = response.cancelledJobIds.length;
  const paused = response.pausedProjectIds.length;

  return {
    tone: 'warning',
    title: 'Đã bật dừng khẩn cấp cho toàn bộ workspace',
    detail: [
      `Cờ dừng đã ghi cho ${response.affectedProjectIds.length} dự án nên máy chủ sẽ không đẩy thêm lệnh bình luận nào.`,
      cancelled === 0
        ? 'Không có lệnh bình luận nào đang chờ để huỷ.'
        : `${cancelled} lệnh bình luận đang chờ đã bị huỷ vĩnh viễn — muốn đăng lại phải soạn bản nháp mới.`,
      paused === 0
        ? 'Không có dự án nào đang chạy để tạm dừng.'
        : `${paused} dự án đang chạy đã chuyển sang tạm dừng.`,
      'Điều máy chủ KHÔNG chứng minh được: một lượt gõ đã bắt đầu trên máy bạn chỉ dừng khi agent nhận được lệnh dừng. Hãy mở bài viết kiểm tra.',
    ].join(' '),
  };
}

/**
 * Why the switch did not move — where the two endpoints stop being symmetric.
 *
 * `POST /comments/emergency-stop` is open to any member; the clear route carries
 * `@Roles('ADMIN')`. So an ordinary member pressing "tắt" gets a 403,
 * and the raw envelope ("FORBIDDEN: …") reads as though the kill switch itself
 * had broken. The direction of each failure is also different, and that is what
 * the tones say: a failed *stop* means nothing was stopped, which is the bad
 * direction and is `danger`; a failed *clear* means the switch is still on,
 * which is merely inconvenient.
 */
export function describeEmergencyStopFailure(
  error: ApiRequestError,
  action: EmergencyStopAction,
): ActionOutcome {
  const specifics = `Chi tiết: “${error.message}”.`;

  if (action === 'ACTIVATE') {
    return {
      tone: 'danger',
      title: 'KHÔNG bật được dừng khẩn cấp',
      detail:
        'Yêu cầu không được máy chủ chấp nhận, nên không có gì bị dừng và mọi lệnh đang chạy vẫn chạy. ' +
        'Thử lại ngay. Nếu vẫn hỏng, thứ duy nhất còn nằm trong tay bạn là tắt agent hoặc đóng cửa sổ Chromium trên chính máy này. ' +
        specifics,
    };
  }

  if (error.code === 'FORBIDDEN') {
    return {
      tone: 'warning',
      title: 'Bạn không có quyền tắt dừng khẩn cấp',
      detail:
        'Bật thì thành viên nào cũng bật được, nhưng tắt là quyền của chủ workspace hoặc quản trị viên. ' +
        'Công tắc vẫn đang bật và bình luận vẫn đang bị chặn — hãy nhờ họ tắt giúp.',
    };
  }

  return {
    tone: 'warning',
    title: 'Chưa tắt được dừng khẩn cấp',
    detail: `Công tắc vẫn đang bật nên chưa có bình luận nào được gửi trở lại. ${specifics}`,
  };
}

/**
 * Model self-assessment as a percentage.
 *
 * `null` is not zero and must never render as "0%". Template and manual
 * comments have no confidence at all — nothing scored them — and "0%" reads as
 * "the model thinks this is terrible", which is the opposite of "không ai chấm".
 * Same rule as `formatScore10` and for the same reason.
 */
export function formatConfidence(value: number | null): string {
  if (value === null) return 'Không áp dụng';
  return `${Math.round(value * 100)}%`;
}
